import { useEffect, useRef, useState } from 'react';
import { ROUTE_EDIT_OCCUPANCY_AGREEMENTS, ROUTE_OCCUPANCY_AGREEMENTS } from '../routes/Routes';
import UpdateOccupancyAgreement from './UpdateOccupancyAgreement';
import { useAuth } from '../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import GeneralFormConfig from '../GeneralFormConfig/GeneralFormConfig';
import { fieldTypeNames } from '../BuildingsPage/Shared/shared';
import GetFetch from '../hooks/GetFetch';
import PutPostPatchFetch from '../hooks/PutPostPatchFetch';
import { LoadingIndicator } from 'devextreme-react/cjs/bar-gauge';
import { formatCustomDate, showNotification } from '../util/_helpers';


const OccupancyAgreementDetails = () => {
    const { title } = ROUTE_EDIT_OCCUPANCY_AGREEMENTS;
    const { user } = useAuth();
    const navigate = useNavigate();
    const { state } = useLocation();

    const [formData, setFormData] = useState({});
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [buildings, setBuildings] = useState([]);
    const [customers, setCustomers] = useState([]);

    const validationGroupRef = useRef(null);


    const getSingleOccupancyAgreementRecordData = async () => {
        setLoading(true);

        try {
            const [customerRes, occupancyRes] = await Promise.all([
                GetFetch("/v1/Customer/GetCustomers"),
                GetFetch(`/v1/OccupancyAgreement/${state}`)
            ]);

            const {
                Errors: customerErrors,
                Message: customerMessage,
                Success: customerSuccess
            } = customerRes;

            const {
                Errors: occupancyAgreementErrors,
                Message: occupancyAgreementMessage,
                Success: occupancyAgreementSuccess
            } = occupancyRes;

            if (customerSuccess) setCustomers(customerMessage);

            if (occupancyAgreementSuccess) setFormData(occupancyAgreementMessage);

            if (customerErrors.length > 0) console.error("Customer errors:", customerErrors);

            if (occupancyAgreementErrors.length > 0) console.error("Occupancy Agreement errors:", occupancyAgreementErrors);

        } 
        catch (error) {
            console.error(error);
        } 
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getSingleOccupancyAgreementRecordData();
    }, []);
    
    const getBuildingNumbers = async (c) => {
        try {
            if (formData.BuildingNumber == undefined) setLoading(true);

            const { Errors, Message, Success } = await GetFetch(`/v1/Building/Customer/${c}`);

            if (Success) {
                setBuildings(Message);

                if (Message.length === 1) {
                    const building = Message[0];
                    setFormData(prev => ({
                        ...prev,
                        BuildingId: building.BuildingId,
                        BuildingNumber: building.SequenceNumber
                    }));
                }
            }
        } catch (error) {
            console.error(error)
        }
        setLoading(false);
    };

    useEffect(() => {
        if (!customers.length) return;
        if (!formData?.CustomerAccountId) return;

        const defaultCustomer = customers.find(
            c => c.CustomerAccountId === formData.CustomerAccountId
        );
        if (!defaultCustomer) return;

        setFormData(prev => ({
            ...prev,
            CustomerAccountId: defaultCustomer.CustomerAccountId,
            AgencyName: defaultCustomer.AgencyName
        }));

        getBuildingNumbers(defaultCustomer.CustomerAccountId);
    }, [customers, formData?.CustomerAccountId]);


    const handleEditButtonClick = () => editing === true ? setEditing(false) : setEditing(true);
    
    const updateOccupancyAgreement = async () => {
        if (!validationGroupRef.current) {
            console.error("ValidationGroup ref is not set");
            return;
        };

        const validationResult = validationGroupRef.current.instance().validate();
        if (!validationResult.isValid) {
            showNotification(
                "Please add values for all required fields and try again.",
                false,
                5000,
                500
            );
            return;
        };

        console.log("obj",
                            {
                    "BuildingByCustomerId": formData.BuildingByCustomerId,
                    "BuildingId": formData.BuildingId,
                    "CreatedBy": formData.CreatedBy,
                    "CreatedDate": formData.CreatedDate,
                    "CustomerAccountId": formData.CustomerAccountId,
                    "EffectiveDate": formData.EffectiveDate,
                    "ExpirationDate": formData.ExpirationDate,
                    "Inactive": true,
                    "JURentSqFt": formData.JURentSqFt,
                    "LeaseNumber": formData.LeaseNumber,
                    "MemberName": formData.MemberName,
                    "OANumber": formData.OANumber,
                    "OccupancyAgreementId": formData.OccupancyAgreementId,
                    "RUFactor": formData.RUFactor,
                    "TotalRentSqFt": formData.TotalRentSqFt,
                    "UpdatedDate": new Date().toISOString(),
                    "UpdatedBy": user.email,
                }
        )

        try {
            const { Success, Message, Errors } = await PutPostPatchFetch(
                "/v1/OccupancyAgreement",
                "PUT",
                {
                    "BuildingByCustomerId": formData.BuildingByCustomerId,
                    "BuildingId": formData.BuildingId,
                    "CreatedBy": formData.CreatedBy,
                    "CreatedDate": formData.CreatedDate,
                    "CustomerAccountId": formData.CustomerAccountId,
                    "EffectiveDate": formatCustomDate(formData.EffectiveDate),
                    "ExpirationDate": formatCustomDate(formData.ExpirationDate),
                    "Inactive": true,
                    "JURentSqFt": formData.JURentSqFt,
                    "LeaseNumber": formData.LeaseNumber.toString(),
                    "MemberName": formData.MemberName,
                    "OANumber": formData.OANumber.toString(),
                    "OccupancyAgreementId": formData.OccupancyAgreementId,
                    "RUFactor": formData.RUFactor,
                    "TotalRentSqFt": formData.TotalRentSqFt,
                    "UpdatedDate": formatCustomDate(),
                    "UpdatedBy": user.email,
                }
            );

            if (Success) {
                showNotification(
                    `Occupancy Agreement ${state} was updated, successfully. Returning to previous page.`,
                    true,
                    4000,
                    500
                );
                setTimeout(() => navigate(ROUTE_OCCUPANCY_AGREEMENTS.withSlash), 5000)
                console.log(
                    Success,
                    Message
                )
            };

            if (Errors.length > 0) {
                showNotification(
                    `Unable to update Occupancy Agreement ${state}. Please ensure that the values provided are correct and try again.`,
                    false,
                    4000,
                    500
                );
            };

        } catch (error) {
            console.error(error);
        };
    };

    const formProps = {
        className: classNames(
            { 
                'plain-styled-form': true, 
                // 'view-mode': !editing 
            }
        ),
        groupedFields: [
            {
                groupCaption: "General Information",
                fields: [
                    {
                        fieldType: fieldTypeNames.textBox,
                        label: "OA Number",
                        onValueChanged: (e) => {
                            setFormData({
                                ...formData,
                                OANumber: e.value
                            })
                        },
                        readOnly: !editing,
                        value: formData.OANumber,
                    },
                    {
                        fieldType: fieldTypeNames.dateBox,
                        label: "Effective Date",
                        onValueChanged: (e) => {
                            setFormData({
                                ...formData,
                                EffectiveDate: e.value
                            })
                        },
                        readOnly: !editing,
                        value: formData.EffectiveDate,
                    },
                    {
                        fieldType: fieldTypeNames.dateBox,
                        label: "Expiration Date",
                        onValueChanged: (e) => {
                            setFormData({
                                ...formData,
                                ExpirationDate: e.value
                            })
                        },
                        readOnly: !editing,
                        value: formData.ExpirationDate,
                    },
                    {
                        fieldType: fieldTypeNames.textBox,
                        label: "Member Name",
                        onValueChanged: (e) => {
                            setFormData({
                                ...formData,
                                MemberName: e.value
                            })
                        },
                        readOnly: !editing,
                        value: formData.MemberName,
                    },
                    {
                        displayExpr: "AgencyName",
                        fieldType: fieldTypeNames.selectBox,
                        label: "Customer",
                        options: customers,
                        readOnly: !editing,
                        valueExpr: "CustomerAccountId",
                        value: formData.CustomerAccountId,
                        onValueChanged: ({ value }) => {
                            const selectedCustomer = customers.find(
                                c => c.CustomerAccountId === value
                            );

                            if (!selectedCustomer) return;
                            
                            setFormData(prev => ({
                                ...prev,
                                CustomerAccountId: selectedCustomer.CustomerAccountId,
                                AgencyName: selectedCustomer.AgencyName,
                                BuildingId: null,
                                BuildingNumber: '' 
                            }));
                            getBuildingNumbers(selectedCustomer.CustomerAccountId);
                        }
                    },
                    {
                        displayExpr: "SequenceNumber",
                        fieldType: fieldTypeNames.selectBox,
                        label: "Building Number",
                        options: buildings,
                        readOnly: !editing,
                        valueExpr: "SequenceNumber",
                        value: formData.BuildingNumber,
                        onValueChanged: ({ value }) => {
                            if (value == null) return;
                            
                            const selectedBuilding = buildings.find(
                                b => b.SequenceNumber === value
                            );

                            setFormData(prev => ({
                                ...prev,
                                BuildingNumber: value,
                                BuildingId: selectedBuilding
                                    ? selectedBuilding.BuildingId
                                    : prev.BuildingId
                            }));
                        }
                    },
                ]
            },
            {
                groupCaption: "Billing Information",
                fields: [
                    {
                        fieldType: fieldTypeNames.numberBox,
                        label: "Joint Use Rent Sq. Ft.",
                        onValueChanged: (e) => {
                            setFormData({
                                ...formData,
                                JURentSqFt: e.value
                            })
                        },
                        readOnly: !editing,
                        value: formData.JURentSqFt,
                    },
                    {
                        fieldType: fieldTypeNames.numberBox,
                        label: "Lease Number",
                        onValueChanged: (e) => {
                            setFormData({
                                ...formData,
                                LeaseNumber: e.value
                            })
                        },
                        readOnly: !editing,
                        value: formData.LeaseNumber,
                    },
                    {
                        fieldType: fieldTypeNames.numberBox,
                        label: "Rent Usage Factor",
                        onValueChanged: (e) => {
                            setFormData({
                                ...formData,
                                RUFactor: e.value
                            })
                        },
                        readOnly: !editing,
                        value: formData.RUFactor,
                    },
                    {
                        fieldType: fieldTypeNames.numberBox,
                        label: "Total Rent Sq. Ft.",
                        onValueChanged: (e) => {
                            setFormData({
                                ...formData,
                                TotalRentSqFt: e.value
                            })
                        },
                        readOnly: !editing,
                        value: formData.TotalRentSqFt,
                    }
                ]
            }
        ],
        loadingIndicatorState: loading,
        loadingIndicatorMessage: "Loading Customer and Building Number data. Please wait...",
        userId: user,
        title: `${title}: ${state}`,
        toolbarConfiguration: {
            children: undefined,
            editing: editing,
            onBackClick: () => navigate(ROUTE_OCCUPANCY_AGREEMENTS.withSlash),
            onCancelClick: () => setEditing(prev => !prev),
            toggleEditing: () => setEditing(prev => !prev),
            onSaveClick: updateOccupancyAgreement,
        },
        validationGroupRef: validationGroupRef
    };

    return <GeneralFormConfig { ...formProps } />;
}
export default OccupancyAgreementDetails;
